/**
 * Broadcast WebSocket Service (Singleton)
 * Manages WebSocket connection for real-time broadcast updates
 * Uses portal parameter instead of user_id (broadcasts are portal-wide)
 */

import { getToken } from '../utils/tokenStorage';

// Get store dynamically to avoid circular dependency
const getStore = () => {
  return require('../../store').store;
};

const getWsBaseUrl = () => {
  if (process.env.REACT_APP_WS_BASE_URL) {
    return process.env.REACT_APP_WS_BASE_URL;
  }
  if (window.location.hostname === 'fleetcamagile.com') {
    return 'wss://fleetcamagile.com';
  }
  return 'ws://localhost:8000';
};

class BroadcastService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.heartbeatInterval = null;
    this.portal = null;
    this.isConnected = false;
  }

  initializeWebSocket(portal = 'bureau') {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[BroadcastService] Already connected');
      return;
    }

    this.portal = portal;

    try {
      const WS_BASE_URL = getWsBaseUrl();
      const token = getToken();
      const wsUrl = `${WS_BASE_URL}/ws/broadcasts/?portal=${portal}&token=${token || ''}`;
      console.log('[BroadcastService] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[BroadcastService] Connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'broadcasts/setConnected', payload: true });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[BroadcastService] Error parsing message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[BroadcastService] WebSocket error:', error);
        this.isConnected = false;
      };

      this.ws.onclose = () => {
        console.log('[BroadcastService] Connection closed');
        this.isConnected = false;
        this.stopHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'broadcasts/setConnected', payload: false });
        }

        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[BroadcastService] Connection error:', error);
      this.isConnected = false;
    }
  }

  handleMessage(data) {
    const store = getStore();
    if (!store || !store.dispatch) return;

    switch (data.type) {
      case 'initial_data':
        console.log(`[BroadcastService] Initial data: ${data.broadcasts?.length} broadcasts`);
        store.dispatch({
          type: 'broadcasts/setBroadcasts',
          payload: data.broadcasts || [],
        });
        break;

      case 'broadcast_update':
        if (data.action === 'created') {
          console.log('[BroadcastService] New broadcast:', data.broadcast.title);
          store.dispatch({
            type: 'broadcasts/addBroadcast',
            payload: data.broadcast,
          });
        } else if (data.action === 'updated') {
          store.dispatch({
            type: 'broadcasts/updateBroadcast',
            payload: data.broadcast,
          });
        }
        break;

      case 'heartbeat':
      case 'pong':
        break;

      default:
        console.log('[BroadcastService] Unknown message type:', data.type);
    }
  }

  scheduleReconnect() {
    if (this.reconnectInterval || !this.portal) return;

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(`[BroadcastService] Reconnecting in ${delay / 1000}s`);

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.initializeWebSocket(this.portal);
    }, delay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  acknowledgeBroadcast(broadcastId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'acknowledge',
          broadcast_id: broadcastId,
        })
      );
      console.log(`[BroadcastService] Acknowledged broadcast ${broadcastId}`);
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    this.stopHeartbeat();
    this.isConnected = false;
    this.portal = null;

    console.log('[BroadcastService] Disconnected');
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState,
    };
  }
}

// Create singleton instance
const broadcastService = new BroadcastService();

export default broadcastService;
